import { ROUTES } from 'AppRoutes';
import { useValidateCenter } from 'hooks/api/useAdminAuth';
import useApiErrorHandling from 'hooks/util/useApiErrorHandler';
import useValidation from 'hooks/util/useValidation';
import { CenterValidationFormData } from 'pages/CenterValidation/components/CenterValidationDialog';
import CenterValidationSchema from 'pages/CenterValidation/schema/CenterValidationSchema';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminLoginResponse, GenericApiResponse, ValidateCenterResponse } from 'services/api';
import { ErrorCode, getErrorMessageFromCode } from 'utils/errorMessages';

interface ValidationParameters {
  programCode: string;
  password: string;
  adminCode: string;
}

interface TestParameters extends ValidationParameters {
  abortCode: string;
  startCode: string;
}

export type ErrorsState = Partial<TestParameters>;

const mapCodeToFieldErrors = (code: ErrorCode) => {
  const errors: ErrorsState = {};
  switch (code as ErrorCode) {
    case ErrorCode.InvalidAdminCode: {
      errors.adminCode = getErrorMessageFromCode(code);
      break;
    }
    case ErrorCode.InvalidProgramCode: {
      errors.programCode = getErrorMessageFromCode(code);
      break;
    }
    case ErrorCode.IncorrectPassword: {
      errors.password = getErrorMessageFromCode(code);
      break;
    }
    case ErrorCode.InvalidAbortCode: {
      errors.abortCode = getErrorMessageFromCode(code);
      break;
    }
    case ErrorCode.InvalidStartCode: {
      errors.startCode = getErrorMessageFromCode(code);
      break;
    }
    case ErrorCode.AdminAndProgramCodeMismatch: {
      errors.adminCode = getErrorMessageFromCode(code);
      break;
    }
    default:
  }
  return errors;
};

const getErrors = (errorStates: ErrorsState[]) => {
  return (
    errorStates.find((errorState) =>
      Object.keys(errorState).some((key) => errorState[key as keyof ErrorsState]),
    ) || {}
  );
};

const useCenterValidation = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { apiErrors, handleApiError, clearApiError } = useApiErrorHandling(mapCodeToFieldErrors);

  const { errors: validationErrors, validate, clearError } = useValidation(CenterValidationSchema);

  const resetValidation = useCallback(
    (type?: string) => {
      if (type) clearApiError(type);
      if (type) clearError(type);
    },
    [clearError, clearApiError],
  );

  const mutateOptions = {
    onMutate: () => setIsSubmitting(true),
    onError: () => setIsSubmitting(false),
    onSuccess: (
      data: GenericApiResponse<ValidateCenterResponse> | GenericApiResponse<AdminLoginResponse>,
    ) => {
      if (data.data.code) {
        handleApiError(data.data.code);
      }
      setIsSubmitting(false);
    },
  };
  const { mutate: validateCenter } = useValidateCenter(mutateOptions);
  const onSubmitCenterDetails = useCallback(
    (
      parameters: ValidationParameters,
      onSuccess?: (
        data: GenericApiResponse<ValidateCenterResponse>,
        parameters: ValidationParameters,
      ) => void,
    ) => {
      if (validate(parameters)) {
        validateCenter(parameters, { onSuccess });
      }
    },
    [validate, validateCenter],
  );

  const onProceed = useCallback(
    (validationResponse: ValidateCenterResponse, parameters: CenterValidationFormData) => {
      const {
        accessToken,
        adminId,
        refreshToken,
        splashScreenImage,
        centerCode,
        centerName,
        programCode,
      } = validationResponse;
      const { adminCode } = parameters;
      localStorage.setItem('accessToken', accessToken.token);
      localStorage.setItem('adminId', adminId);
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('splashScreenImage', JSON.stringify(splashScreenImage));
      const adminDetails = {
        centerCode,
        centerName,
        programCode,
        adminCode,
      };
      localStorage.setItem('adminDetails', JSON.stringify(adminDetails));

      navigate(ROUTES.STUDENT_LOGIN);
    },
    [navigate],
  );

  return {
    onSubmitCenterDetails,
    errors: getErrors([validationErrors, apiErrors]),
    isSubmitting,
    resetValidation,
    onProceed,
  };
};

export default useCenterValidation;
